import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Database, Table, Search, ChevronLeft, ChevronRight, RefreshCw, Download, Copy, Check } from 'lucide-react';

export default function DatabaseBrowser() {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [showSchemaViewer, setShowSchemaViewer] = useState(false);
  const [schemaData, setSchemaData] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadTables();
  }, []);

  const loadTables = async () => {
    try {
      setLoading(true);
      const sessionId = sessionStorage.getItem('sessionId');
      const response = await fetch('/api/db/tables', {
        headers: { 'x-session-token': sessionId }
      });
      
      if (!response.ok) throw new Error('Failed to fetch tables');
      
      const data = await response.json();
      setTables(data.tables);
    } catch (error) {
      console.error('Error loading tables:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTableData = async (tableName, page = 1) => {
    try {
      setLoading(true);
      const sessionId = sessionStorage.getItem('sessionId');
      const response = await fetch(`/api/db/table/${tableName}?page=${page}&limit=${itemsPerPage}`, {
        headers: { 'x-session-token': sessionId }
      });
      
      if (!response.ok) throw new Error('Failed to fetch table data');
      
      const data = await response.json();
      setTableData(data);
      setSelectedTable(tableName);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error loading table data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportSchema = async (format = 'json') => {
    try {
      setSchemaLoading(true);
      const sessionId = sessionStorage.getItem('sessionId');
      const response = await fetch(`/api/db/schema?format=${format}`, {
        headers: { 'x-session-token': sessionId }
      });
      
      if (!response.ok) throw new Error('Failed to export schema');
      
      if (format === 'markdown') {
        const markdown = await response.text();
        downloadFile(markdown, 'database-schema.md', 'text/markdown');
      } else {
        const data = await response.json();
        setSchemaData(data);
        setShowSchemaViewer(true);
      }
    } catch (error) {
      console.error('Error exporting schema:', error);
      alert('Failed to export schema: ' + error.message);
    } finally {
      setSchemaLoading(false);
    }
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const copySchemaToClipboard = async () => {
    if (schemaData) {
      try {
        await navigator.clipboard.writeText(JSON.stringify(schemaData, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Error copying to clipboard:', error);
      }
    }
  };

  const downloadSchemaJSON = () => {
    if (schemaData) {
      downloadFile(
        JSON.stringify(schemaData, null, 2),
        'database-schema.json',
        'application/json'
      );
    }
  };

  const filteredTables = tables.filter(table =>
    table.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const goToPage = (page) => {
    if (selectedTable) {
      loadTableData(selectedTable, page);
    }
  };

  if (loading && tables.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-full flex">
      {/* Tables List Sidebar */}
      <div className="w-80 bg-white border-r overflow-y-auto">
        <div className="p-4 border-b">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Database className="w-6 h-6 text-blue-600" />
            Database Tables
          </h2>
          
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search tables..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Button
            onClick={() => exportSchema('json')}
            disabled={schemaLoading}
            className="w-full"
            variant="outline"
          >
            {schemaLoading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Database className="w-4 h-4 mr-2" />
            )}
            Export Schema
          </Button>
        </div>

        <div className="p-2">
          {filteredTables.map((table) => (
            <div
              key={table.name}
              onClick={() => loadTableData(table.name, 1)}
              className={`p-3 mb-1 rounded cursor-pointer hover:bg-blue-50 transition-colors ${
                selectedTable === table.name ? 'bg-blue-100 border-l-4 border-blue-600' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Table className="w-4 h-4 text-gray-600 flex-shrink-0" />
                  <span className="font-medium text-sm break-all">{table.name}</span>
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <Badge variant="secondary" className="text-xs">
                  {table.rowCount.toLocaleString()} rows
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {table.columnCount} cols
                </Badge>
              </div>
              {table.error && (
                <p className="text-xs text-red-600 mt-1">Error: {table.error}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Table Data View */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {!selectedTable ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <Database className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Select a table to view its data</p>
              <p className="text-sm mt-2">Found {tables.length} tables in the database</p>
            </div>
          </div>
        ) : tableData ? (
          <>
            {/* Header */}
            <div className="bg-white border-b p-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Table className="w-6 h-6 text-blue-600" />
                  {tableData.tableName}
                </h2>
                <Button
                  onClick={() => loadTableData(selectedTable, currentPage)}
                  size="sm"
                  variant="outline"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>Total Rows: <strong>{tableData.totalRows.toLocaleString()}</strong></span>
                <span>Columns: <strong>{tableData.columns.length}</strong></span>
                <span>Page: <strong>{currentPage} of {tableData.totalPages}</strong></span>
              </div>
            </div>

            {/* Pagination Controls */}
            <div className="bg-white border-b p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Rows per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                    loadTableData(selectedTable, 1);
                  }}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1 || loading}
                  size="sm"
                  variant="outline"
                >
                  First
                </Button>
                <Button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  size="sm"
                  variant="outline"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                
                <span className="text-sm px-2">
                  {currentPage} / {tableData.totalPages}
                </span>
                
                <Button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === tableData.totalPages || loading}
                  size="sm"
                  variant="outline"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => goToPage(tableData.totalPages)}
                  disabled={currentPage === tableData.totalPages || loading}
                  size="sm"
                  variant="outline"
                >
                  Last
                </Button>
              </div>
            </div>

            {/* Table Content */}
            <div className="flex-1 overflow-auto bg-gray-50 p-4">
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        {tableData.columns.map((col) => (
                          <th
                            key={col.column_name}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            <div className="flex flex-col">
                              <span>{col.column_name}</span>
                              <span className="text-xs text-gray-400 font-normal normal-case">
                                {col.data_type}
                              </span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tableData.data.map((row, rowIndex) => (
                        <tr key={rowIndex} className="hover:bg-gray-50">
                          {tableData.columns.map((col) => (
                            <td
                              key={col.column_name}
                              className="px-4 py-3 text-sm text-gray-900"
                            >
                              <div className="max-w-md truncate" title={String(row[col.column_name] ?? '')}>
                                {row[col.column_name] === null ? (
                                  <span className="text-gray-400 italic">NULL</span>
                                ) : typeof row[col.column_name] === 'object' ? (
                                  <span className="text-xs text-gray-600">
                                    {JSON.stringify(row[col.column_name])}
                                  </span>
                                ) : typeof row[col.column_name] === 'boolean' ? (
                                  <Badge variant={row[col.column_name] ? 'default' : 'secondary'}>
                                    {row[col.column_name] ? 'TRUE' : 'FALSE'}
                                  </Badge>
                                ) : (
                                  String(row[col.column_name])
                                )}
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        )}
      </div>

      {/* Schema Viewer Modal */}
      {showSchemaViewer && schemaData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Database className="w-6 h-6 text-blue-600" />
                  Database Schema
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {schemaData.totalTables} tables • Generated: {new Date(schemaData.generatedAt).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={copySchemaToClipboard}
                  size="sm"
                  variant="outline"
                  disabled={copied}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy JSON
                    </>
                  )}
                </Button>
                <Button
                  onClick={downloadSchemaJSON}
                  size="sm"
                  variant="outline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download JSON
                </Button>
                <Button
                  onClick={() => exportSchema('markdown')}
                  size="sm"
                  variant="outline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Markdown
                </Button>
                <Button
                  onClick={() => setShowSchemaViewer(false)}
                  size="sm"
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6">
              <div className="bg-gray-50 rounded-lg p-4 font-mono text-xs overflow-x-auto">
                <pre className="whitespace-pre-wrap break-words">
                  {JSON.stringify(schemaData, null, 2)}
                </pre>
              </div>

              {/* Summary Cards */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(schemaData.schema).map(([tableName, table]) => (
                  <Card key={tableName} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Table className="w-4 h-4 text-blue-600" />
                        {tableName}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Rows:</span>
                          <span className="font-medium">{table.rowCount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Columns:</span>
                          <span className="font-medium">{table.columns.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Size:</span>
                          <span className="font-medium">{table.size}</span>
                        </div>
                        {table.primaryKeys.length > 0 && (
                          <div className="pt-2 border-t">
                            <span className="text-gray-600">Primary Keys:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {table.primaryKeys.map((pk, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {pk.column}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {table.foreignKeys.length > 0 && (
                          <div className="pt-2 border-t">
                            <span className="text-gray-600">Foreign Keys:</span>
                            <div className="text-xs text-gray-500 mt-1">
                              {table.foreignKeys.length} relationship(s)
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
